import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import QRCode from 'qrcode.react'
import { LeftArrowIcon } from 'components/ui-kit/Icons/svg/index'
import CryptoLogo from 'components/ui-kit/CryptoLogo'
import { blockInvalidChar, cryptoToFiat, handleCopyCode } from 'utils/helperFunctions.utils'
import { generateWalletAddress } from 'redux-thunk/thunk/user.thunk'
import { CRYPTO_COINS, LOADER_HANDLER_TYPES, ROUTE_PATHS } from 'constants/index'
import Loader from 'components/ui-kit/Loader/index'
import { setShowCashierPopup } from 'redux-thunk/redux/slices/settings.slice'
import { useOnramper } from 'hooks/useOnramper'
import DepositBuyCrypto from './DepositBuyCrypto'

// Double background (padding-box fill + border-box gradient) paints the gold
// border. Keep each arbitrary class as one unbroken literal for Tailwind.
const GOLD_INPUT =
  'relative w-full box-border overflow-hidden rounded-xl border border-solid border-transparent ' +
  'px-4 py-4 text-base text-white shadow-none outline-none md:py-5 ' +
  'shadow-[inset_0_1px_0_rgb(var(--pm-gold)/0.06)] ' +
  '[background:linear-gradient(145deg,#472e00,#090b0b_25%)_padding-box,linear-gradient(135deg,#8a6500,#ffd700,#b8860b,#b8860b,#8a6500)_border-box] ' +
  'placeholder:text-[var(--gray-20)] focus:border-transparent focus:shadow-none ' +
  'read-only:text-white max-md:pr-[5.7rem]'

const FORM_INPUT =
  'h-12 w-full box-border rounded-[0.625rem] ' +
  'bg-[var(--gray-90)] py-[0.438rem] pl-10 pr-3 text-base text-white shadow-none outline-none ' +
  'placeholder:text-[var(--gray-20)] border-[#f7b731] focus:border-[#f7b731] focus:bg-[var(--gray-90)] focus:text-white focus:shadow-none'

const COPY_BTN =
  'absolute right-3 bottom-0 top-0 my-auto h-10 min-w-[120px] cursor-pointer ' +
  'items-center justify-center whitespace-nowrap rounded-lg border-0 ' +
  'bg-[linear-gradient(#EFD43C,#CB7D00)] px-6 py-1 text-[0.813rem] font-medium text-white ' +
  'shadow-none transition-all duration-300 hover:bg-[linear-gradient(#EFD43C,#CB7D00)] ' +
  'focus:bg-[linear-gradient(#EFD43C,#CB7D00)] active:scale-[0.98] active:opacity-80 ' +
  'max-md:min-w-[106px] max-sm:min-w-0 max-sm:px-3'

const DepositCurrencySection = ({ selectedWallet, setSelectedWallet }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { [LOADER_HANDLER_TYPES.walletAddress]: loading } = useSelector(
    (state) => state.loader
  )
  const { showOnramperWidget } = useOnramper()
  const [fiatAmount, setFiatAmount] = useState('')
  const [cryptoAmount, setCryptoAmount] = useState('')
  const [headerImgError, setHeaderImgError] = useState(false)
  const [cryptoImgError, setCryptoImgError] = useState(false)
  const PRIMARY_FIAT = 'USD'
  const isUsdt = (selectedWallet?.currency?.code || selectedWallet?.currency?.name || '').toUpperCase().includes('USDT') || (selectedWallet?.currency?.code || selectedWallet?.currency?.name || '').toUpperCase().includes('TETHER')

  const generateAddress = (wallet) => {
    if (!wallet?.walletAddress) {
      const params = {
        currencyCode: wallet.currency.code,
        currencyId: Number(wallet.currency.id)
      }
      dispatch(generateWalletAddress({ params }))
    }
  }

  const handleChangeCrypto = (e) => {
    const val = e.target.value
    setCryptoAmount(val)
    const coinCode = CRYPTO_COINS[selectedWallet?.currency?.code] || selectedWallet?.currency?.code || 'BTC'

    cryptoToFiat(coinCode, PRIMARY_FIAT, val)
      .then((res) => {
        setFiatAmount(!res ? '' : res)
      })
      .catch((e) => {
        console.log(e)
      })
  }

  const handleChangeFiat = (e) => {
    const val = e.target.value
    setFiatAmount(val)
    const coinCode = CRYPTO_COINS[selectedWallet?.currency?.code] || selectedWallet?.currency?.code || 'BTC'
    cryptoToFiat(PRIMARY_FIAT, coinCode, val)
      .then((res) => {
        setCryptoAmount(!res ? '' : res)
      })
      .catch((e) => {
        console.log(e)
      })
  }

  useEffect(() => {
    if (selectedWallet) {
      generateAddress(selectedWallet)
    }
  }, [selectedWallet])

  if (loading) {
    return <Loader variant={LOADER_HANDLER_TYPES.content} />
  }

  return (
    <div>
      <div className='mb-6 flex items-center max-md:mb-4 max-md:flex-col max-md:items-start max-md:gap-y-4'>
        <h3 className='my-0 flex items-center text-xl font-bold uppercase text-white max-md:text-base'>
          <NavLink
            to='#'
            className='p-0 [&_svg]:mr-4 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:brightness-[10]'
            onClick={() => {
              setSelectedWallet(null)
            }}
          >
            <LeftArrowIcon />
          </NavLink>
          <div className='mr-2.5 inline-flex h-[30px] w-[30px] items-center justify-center rounded-full [&_img]:h-full [&_img]:object-cover'>
            {selectedWallet?.currency?.image && !headerImgError && !isUsdt
              ? (
                <img
                  src={selectedWallet?.currency?.image}
                  alt={selectedWallet?.currency?.name || 'Currency'}
                  onError={() => setHeaderImgError(true)}
                />
                )
              : (
                <CryptoLogo code={selectedWallet?.currency?.code} name={selectedWallet?.currency?.name} size={24} />
                )}
          </div>
          {t('deposit')} {selectedWallet?.currency?.name}
        </h3>
        <NavLink
          to={ROUTE_PATHS.DEPOSIT}
          className='pl-4 text-base font-medium leading-none text-[var(--blue-300)] underline'
          onClick={() => {
            dispatch(setShowCashierPopup(false))
          }}
        >
          {t('viewTransactions')}
        </NavLink>
      </div>
      <div className='mb-2 rounded-[0.625rem] p-6 text-[var(--gray-20)] max-md:p-4'>
        <div className='md:mb-2 md:flex'>
          <p className='mb-0 pr-8 text-base font-normal leading-relaxed text-[var(--gray-20)]'>
            {t('sendAmountInCashier', {
              currency: selectedWallet?.currency?.name
            })}
          </p>
          {selectedWallet?.walletAddress && (
            <div className='max-md:my-4 [&_canvas]:border-[5px] [&_canvas]:border-solid [&_canvas]:border-white'>
              <QRCode value={selectedWallet?.walletAddress} />
            </div>
          )}
        </div>
        <div>
          <div className='relative'>
            <label htmlFor='newUser' className='mb-2 block text-sm max-md:text-xs'>
              {t('yourPersonalDepositAddress', {
                currency: selectedWallet?.currency?.name
              })}
            </label>
            <div className='relative'>
              <input
                type='text'
                className={GOLD_INPUT}
                id='newUser'
                value={selectedWallet?.walletAddress}
                placeholder={t('yourPersonalDepositAddress', {
                  currency: selectedWallet?.currency?.name
                })}
                readOnly
              />
              <button
                type='button'
                className={COPY_BTN}
                onClick={() =>
                  handleCopyCode(
                    selectedWallet?.walletAddress,
                    t('depositCopied')
                  )}
              >
                <span className='hidden md:block'>{t('copyAddress')}</span>
                <span className='md:hidden'>{t('copy')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='mb-2 rounded-[0.625rem] p-6 text-[var(--gray-20)] max-md:p-4'>
        <div className='md:flex md:items-center md:justify-center'>
          <div className='relative w-full'>
            <input
              type='number'
              className={FORM_INPUT}
              id='fiat'
              placeholder={t('topHeaderAmount')}
              value={fiatAmount}
              onChange={(e) => {
                handleChangeFiat(e)
              }}
              onKeyDown={blockInvalidChar}
            />
            <img
              src='/assets/images/coin_wallet.png'
              alt='Coin'
              className='absolute left-4 top-1/2 my-auto h-5 w-5 object-contain'
              style={{
                transform: 'translateY(-50%)'
              }}
            />
          </div>
          <span className='px-4 text-white'>=</span>
          <div className='relative w-full'>
            <input
              type='number'
              className={FORM_INPUT}
              id='crypto'
              placeholder={t('topHeaderAmount')}
              value={cryptoAmount}
              onChange={(e) => {
                handleChangeCrypto(e)
              }}
              onKeyDown={blockInvalidChar}
            />
            {selectedWallet?.currency?.image && !cryptoImgError && !isUsdt
              ? (
                <img
                  src={selectedWallet?.currency?.image}
                  alt='Coin'
                  className='absolute left-4 top-1/2 my-auto h-5 w-5 object-contain'
                  style={{
                    transform: 'translateY(-50%)'
                  }}
                  onError={() => setCryptoImgError(true)}
                />
                )
              : (
                <span className='absolute left-4 top-1/2 my-auto h-5 w-5 [&_svg]:h-5 [&_svg]:w-5'  style={{
                  transform: 'translateY(-50%)'
                }}>
                  <CryptoLogo code={selectedWallet?.currency?.code} name={selectedWallet?.currency?.name} size={20} />
                </span>
                )}
          </div>
        </div>
        <p className='mt-3 p-0 text-center text-base font-normal leading-relaxed text-[var(--gray-20)]'>
          {t('theValueOfCurrencyMayChange', {
            currency: selectedWallet?.currency?.code
          })}
        </p>
      </div>
      <DepositBuyCrypto currencyName={selectedWallet?.currency?.name} buttonType='primary' showOnramperWidget={showOnramperWidget} currencyCode={selectedWallet?.currency?.code} />
    </div>
  )
}

export default DepositCurrencySection
